import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import { useToast } from '@pulsarml/avalon-ui';
import { I18n } from '@utils/i18n';
import { validateToken } from '@utils/jwtValidate';
import awsmobile, { customProvider } from '../../../aws-exports';
import { Logger } from '_clean-code/utils/logger';
import { AUTH_USER_TOKEN_KEY } from '../../../constants';
import { authorizeByToken } from '@utils/amplify/authorizeByToken';
import posthog from 'posthog-js';

interface ILoginCredentials {
  email: string;
  password: string;
}

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const login = useMutation({
    mutationFn: async (credentials: ILoginCredentials) => {
      const user = await Auth.signIn(credentials.email, credentials.password);
      const token = user.signInUserSession.accessToken.jwtToken;

      localStorage.setItem(AUTH_USER_TOKEN_KEY, token);

      return user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        variant: 'success',
        title: I18n.t('successfully_logged')
      });
    },
    onError: (error) => {
      Logger.error('useAuth::login', error);
      toast({
        variant: 'error',
        title: `Error ${error.message}`
      });
    }
  });

  const loginWithOkta = useMutation({
    mutationFn: async () => {
      await Auth.federatedSignIn({
        customProvider
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: (error) => {
      Logger.error('useAuth::loginWithOkta', error);
      toast({
        variant: 'error',
        title: `Error ${error.message}`
      });
    }
  });

  const logout = useMutation({
    mutationFn: async () => {
      await Auth.signOut();
      window.localStorage.clear();
      window.location.href = '/login';
    },
    onSuccess: () => {
      // note: this will clear the cache for all queries
      queryClient.clear();
      posthog.reset();
      Logger.info('useAuth::logout::success');
    },
    onError: (error) => {
      Logger.error('useAuth::logout', error);
      toast({
        variant: 'error',
        title: error.message
      });
    }
  });

  const fragments = new URLSearchParams(window.location.hash.replace('#', ''));
  const idToken = fragments.get('id_token');
  const accessToken = fragments.get('access_token');
  if (accessToken) {
    Logger.info('start authorizeByToken');
    authorizeByToken(
      {
        idToken: idToken ?? '',
        accessToken,
        refreshToken: ''
      },
      {
        userPoolId: awsmobile.aws_user_pools_id,
        clientId: awsmobile.aws_user_pools_web_client_id
      }
    ).then((cognitoUser) => {
      const token = cognitoUser?.signInUserSession?.accessToken?.jwtToken;
      if (token) {
        localStorage.setItem(AUTH_USER_TOKEN_KEY, token);
      }
      Logger.info('end authorizeByToken');
      window.location.replace('#');
      // slice off the remaining '#' in HTML5:
      if (typeof window.history.replaceState == 'function') {
        history.replaceState({}, '', window.location.href.slice(0, -1));
      }
    });
  }

  const isAuthenticated = validateToken(
    fragments.get('access_token') ?? localStorage.getItem(AUTH_USER_TOKEN_KEY),
    true
  );

  return { login, logout, loginWithOkta, isAuthenticated };
};
